import * as THREE from 'three';
import {
	buildDailyFloorHeatmapGridPoints,
	buildFacadeHeatmapGridPoints,
	buildTerrainHeatmapGridPoints,
} from './photonHeatmapGrid';
import {
	createHeatmapLegendStats,
	FLOOR_HEATMAP_NEGATIVE_TOLERANCE,
	FLOOR_HEATMAP_VERTICAL_TOLERANCE,
	getFloorHeatmapHashKey,
	getHeatmapColor,
	getNearbyFloorHashKeys,
	getPercentile,
	HEATMAP_FIXED_SCALE_MAX_WM2,
	PHOTON_HEATMAP_OPACITY,
	PHOTON_HEATMAP_TILE_FILL,
	PhotonHeatmapCellData,
	PhotonHeatmapLegendStats,
	PhotonHeatmapNormalizationMode,
} from './photonHeatmapUtils';
import { PHOTON_PHYSICAL_CONFIG } from './photonPhysicalModel';

interface PackedPhotonMapData {
	positions: Float32Array;
	normals: Float32Array;
	powers: Float32Array;
	bounces: Uint16Array;
	count: number;
}

/**
 * Bin type for accumulated photon energy at grid points
 */
type PhotonHeatmapBin = {
	energy: number;
	count: number;
	point: THREE.Vector3;
	normal: THREE.Vector3;
	group: 'floor' | 'roof' | 'facade' | 'terrain';
	photonPositions: THREE.Vector3[];
};

/**
 * Global reference to the heatmap visual group in scene
 */
let photonHeatmapGroup: THREE.Group | null = null;

/**
 * Whether heatmap rendering is currently enabled
 */
let photonHeatmapEnabled = true;

/**
 * Grid cell size in meters
 */
let photonHeatmapCellSize = 1;

/**
 * Normalization mode for display (raw energy or percentile)
 */
let photonHeatmapNormalizationMode: PhotonHeatmapNormalizationMode = 'raw';

/**
 * Current legend statistics for UI display
 */
let photonHeatmapLegendStats: PhotonHeatmapLegendStats = createHeatmapLegendStats(1, 'raw');

/**
 * Helper function to check if photon bounce should be displayed
 * (workaround: externally injected, see rayTracing.ts)
 */
let shouldShowPhotonBounceFn: ((bounces: number) => boolean) | null = null;

/**
 * Helper function to access global photon maps
 * (workaround: externally injected, see rayTracing.ts)
 */
let getPhotosMapsFunction:
	| (() => { global: PackedPhotonMapData; caustic: PackedPhotonMapData })
	| null = null;

/**
 * Sets the helper functions needed to access photon data
 * (called at initialization from rayTracing.ts)
 */
export function initializeHeatmapRenderer(
	shouldShowPhotonBounceFn_: (bounces: number) => boolean,
	getPhotosMapsFunction_: () => { global: PackedPhotonMapData; caustic: PackedPhotonMapData }
): void {
	shouldShowPhotonBounceFn = shouldShowPhotonBounceFn_;
	getPhotosMapsFunction = getPhotosMapsFunction_;
}

/**
 * Removes heatmap group from scene and disposes THREE resources
 */
export function removePhotonHeatmapFromScene(scene: THREE.Scene): void {
	if (!photonHeatmapGroup) return;
	scene.remove(photonHeatmapGroup);
	photonHeatmapGroup.traverse((child) => {
		if (child instanceof THREE.Mesh) {
			child.geometry?.dispose();
			if (child.material instanceof THREE.Material) {
				child.material.dispose();
			}
		}
	});
	photonHeatmapGroup = null;
}

/**
 * Main heatmap rendering function
 * Bins photons by grid point, computes irradiance, and renders instanced mesh
 */
export async function renderPhotonEnergyHeatmap(scene: THREE.Scene): Promise<void> {
	if (!shouldShowPhotonBounceFn || !getPhotosMapsFunction) {
		console.warn('Heatmap renderer not initialized');
		return;
	}

	removePhotonHeatmapFromScene(scene);

	if (!photonHeatmapEnabled) {
		photonHeatmapLegendStats = createHeatmapLegendStats(
			photonHeatmapCellSize,
			photonHeatmapNormalizationMode,
			{ enabled: false }
		);
		return;
	}

	const { global: globalPhotonMap, caustic: causticPhotonMap } = getPhotosMapsFunction();
	if (globalPhotonMap.count + causticPhotonMap.count === 0) {
		photonHeatmapLegendStats = createHeatmapLegendStats(
			photonHeatmapCellSize,
			photonHeatmapNormalizationMode,
			{ enabled: true }
		);
		return;
	}

	const floorGridPoints = buildDailyFloorHeatmapGridPoints(scene, photonHeatmapCellSize);
	const terrainGridPoints = buildTerrainHeatmapGridPoints(scene, photonHeatmapCellSize);
	const facadeGridPoints = await buildFacadeHeatmapGridPoints(scene, photonHeatmapCellSize);

	// Build spatial hash indices for fast photon-to-grid assignments
	const floorHash = new Map<string, number[]>();
	const roofHash = new Map<string, number[]>();
	const terrainHash = new Map<string, number[]>();
	const facadeHash = new Map<string, number[]>();

	const allGridPoints = [...floorGridPoints, ...terrainGridPoints, ...facadeGridPoints];
	allGridPoints.forEach((gridPoint, index) => {
		const hashPoint =
			gridPoint.group === 'roof'
				? new THREE.Vector3(gridPoint.point.x, gridPoint.point.y, gridPoint.roofZ)
				: new THREE.Vector3(gridPoint.point.x, gridPoint.point.y, gridPoint.floorZ);
		const key = getFloorHeatmapHashKey(hashPoint, photonHeatmapCellSize);

		if (gridPoint.group === 'floor') {
			const bucket = floorHash.get(key);
			if (bucket) bucket.push(index);
			else floorHash.set(key, [index]);
			return;
		}

		if (gridPoint.group === 'roof') {
			const bucket = roofHash.get(key);
			if (bucket) bucket.push(index);
			else roofHash.set(key, [index]);
			return;
		}

		if (gridPoint.group === 'terrain') {
			const bucket = terrainHash.get(key);
			if (bucket) bucket.push(index);
			else terrainHash.set(key, [index]);
			return;
		}

		const bucket = facadeHash.get(key);
		if (bucket) bucket.push(index);
		else facadeHash.set(key, [index]);
	});

	// Accumulate photon energy into bins
	const bins = new Map<string, PhotonHeatmapBin>();
	const maxAssignHorizontalDistance = photonHeatmapCellSize * 1.25;
	const maxAssignVerticalDistance = FLOOR_HEATMAP_VERTICAL_TOLERANCE;
	const maxAssignFacadeNormalDistance = Math.max(0.1, photonHeatmapCellSize * 0.35);
	const maxAssignFacadeTangentialDistance = photonHeatmapCellSize * 1.25;
	const verticalBucketRadius = Math.max(1, Math.ceil(maxAssignVerticalDistance / photonHeatmapCellSize));

	const queryPoint = new THREE.Vector3();
	const processPackedMap = (packedMap: PackedPhotonMapData): void => {
		const { positions, normals, powers, bounces, count } = packedMap;
		for (let i = 0; i < count; i++) {
			const bounce = bounces[i];
			if (!shouldShowPhotonBounceFn!(bounce)) continue;

			const i3 = i * 3;
			const px = positions[i3];
			const py = positions[i3 + 1];
			const pz = positions[i3 + 2];
			let nx = normals[i3];
			let ny = normals[i3 + 1];
			let nz = normals[i3 + 2];

			const normalLenSq = nx * nx + ny * ny + nz * nz;
			if (normalLenSq <= 1e-12) continue;
			const invNormalLen = 1 / Math.sqrt(normalLenSq);
			nx *= invNormalLen;
			ny *= invNormalLen;
			nz *= invNormalLen;

			const absNx = Math.abs(nx);
			const absNy = Math.abs(ny);
			const absNz = Math.abs(nz);
			const isHorizontalHit = absNz >= absNx && absNz >= absNy;
			const isRoofHit = nz < 0;

			if (allGridPoints.length === 0) continue;

			let bestIndex = -1;
			let bestDistanceSq = Number.POSITIVE_INFINITY;
			queryPoint.set(px, py, pz);
			if (isHorizontalHit) {
				const nearbyKeys = getNearbyFloorHashKeys(
					queryPoint,
					photonHeatmapCellSize,
					verticalBucketRadius
				);
				const candidateHashes = [floorHash, roofHash, terrainHash];
				for (const candidateHash of candidateHashes) {
					for (const key of nearbyKeys) {
						const candidateIndices = candidateHash.get(key);
						if (!candidateIndices) continue;
						for (const candidateIndex of candidateIndices) {
							const candidate = allGridPoints[candidateIndex];
							const dx = candidate.point.x - px;
							const dy = candidate.point.y - py;
							const targetPlaneZ = isRoofHit ? candidate.roofZ : candidate.floorZ;
							const signedDistanceToPlane = isRoofHit
								? targetPlaneZ - pz
								: pz - targetPlaneZ;
							if (signedDistanceToPlane < -FLOOR_HEATMAP_NEGATIVE_TOLERANCE) continue;
							if (signedDistanceToPlane > maxAssignVerticalDistance) continue;
							const distanceSq =
								dx * dx + dy * dy + signedDistanceToPlane * signedDistanceToPlane;
							if (distanceSq < bestDistanceSq) {
								bestDistanceSq = distanceSq;
								bestIndex = candidateIndex;
							}
						}
					}
				}
			} else {
				const nearbyKeys = getNearbyFloorHashKeys(queryPoint, photonHeatmapCellSize, 1);
				for (const key of nearbyKeys) {
					const candidateIndices = facadeHash.get(key);
					if (!candidateIndices) continue;
					for (const candidateIndex of candidateIndices) {
						const candidate = allGridPoints[candidateIndex];
						const tx = px - candidate.point.x;
						const ty = py - candidate.point.y;
						const tz = pz - candidate.point.z;
						const normalDistance = Math.abs(
							tx * candidate.normal.x + ty * candidate.normal.y + tz * candidate.normal.z
						);
						if (normalDistance > maxAssignFacadeNormalDistance) continue;
						const tangentialDistanceSq = Math.max(
							0,
							tx * tx + ty * ty + tz * tz - normalDistance * normalDistance
						);
						if (
							tangentialDistanceSq >
							maxAssignFacadeTangentialDistance * maxAssignFacadeTangentialDistance
						) {
							continue;
						}
						const score = tangentialDistanceSq + normalDistance * normalDistance;
						if (score < bestDistanceSq) {
							bestDistanceSq = score;
							bestIndex = candidateIndex;
						}
					}
				}
			}

			if (bestIndex < 0) continue;
			const assignedPoint = allGridPoints[bestIndex];
			if (assignedPoint.group === 'floor' || assignedPoint.group === 'terrain') {
				const horizontalDistance = Math.hypot(assignedPoint.point.x - px, assignedPoint.point.y - py);
				if (horizontalDistance > maxAssignHorizontalDistance) continue;
			}

			const current = bins.get(assignedPoint.key);
			if (current) {
				current.energy += powers[i];
				current.count += 1;
				current.photonPositions.push(new THREE.Vector3(px, py, pz));
			} else {
				bins.set(assignedPoint.key, {
					energy: powers[i],
					count: 1,
					point: assignedPoint.point.clone(),
					normal: assignedPoint.normal.clone(),
					group: assignedPoint.group,
					photonPositions: [new THREE.Vector3(px, py, pz)],
				});
			}
		}
	};

	processPackedMap(globalPhotonMap);
	processPackedMap(causticPhotonMap);

	if (bins.size === 0) {
		photonHeatmapLegendStats = createHeatmapLegendStats(
			photonHeatmapCellSize,
			photonHeatmapNormalizationMode,
			{ enabled: true }
		);
		return;
	}

	// Compute irradiance and luminance for each bin
	const cellAreaM2 = Math.max(1e-6, photonHeatmapCellSize * photonHeatmapCellSize);
	const irradianceByCellKey = new Map<string, number>();
	const luxByCellKey = new Map<string, number>();
	for (const [cellKey, bin] of bins.entries()) {
		const irradianceWm2 = bin.energy / cellAreaM2;
		irradianceByCellKey.set(cellKey, irradianceWm2);
		luxByCellKey.set(
			cellKey,
			irradianceWm2 * PHOTON_PHYSICAL_CONFIG.luminousEfficacyLmPerW
		);
	}

	// Determine normalization range
	let minEnergy = Number.POSITIVE_INFINITY;
	let maxEnergy = 0;
	const energyValues: number[] = [];
	for (const irradiance of irradianceByCellKey.values()) {
		if (irradiance < minEnergy) minEnergy = irradiance;
		if (irradiance > maxEnergy) maxEnergy = irradiance;
		energyValues.push(irradiance);
	}
	if (!Number.isFinite(minEnergy)) minEnergy = 0;
	energyValues.sort((a, b) => a - b);

	const displayMinEnergy =
		photonHeatmapNormalizationMode === 'percentile'
			? getPercentile(energyValues, 0.05)
			: photonHeatmapNormalizationMode === 'fixed'
				? 0
				: minEnergy;
	const displayMaxEnergy =
		photonHeatmapNormalizationMode === 'percentile'
			? getPercentile(energyValues, 0.95)
			: photonHeatmapNormalizationMode === 'fixed'
				? HEATMAP_FIXED_SCALE_MAX_WM2
				: maxEnergy;
	const normalizationRange = Math.max(1e-9, displayMaxEnergy - displayMinEnergy);

	// Build instanced mesh for heatmap visualization
	const group = new THREE.Group();
	group.name = 'PhotonEnergyHeatmap';
	group.userData.isPhotonHeatmap = true;
	group.userData.ignoreSceneClick = true;

	const patchSize = Math.max(0.5, photonHeatmapCellSize * PHOTON_HEATMAP_TILE_FILL);
	const floorSurfaceOffsetDistance = 0.18;
	const roofSurfaceOffsetDistance = 0.18;
	const facadeSurfaceOffsetDistance = 0.02;
	const terrainSurfaceOffsetDistance = 0.12;

	const instancedGeometry = new THREE.PlaneGeometry(1, 1);
	const instancedMaterial = new THREE.MeshBasicMaterial({
		transparent: true,
		opacity: PHOTON_HEATMAP_OPACITY,
		depthWrite: true,
		depthTest: true,
		side: THREE.DoubleSide,
	});

	const instancedMesh = new THREE.InstancedMesh(instancedGeometry, instancedMaterial, bins.size);
	instancedMesh.userData.isPhotonHeatmap = true;
	instancedMesh.userData.ignoreSceneClick = true;
	instancedMesh.frustumCulled = false;
	instancedMesh.renderOrder = 900;

	const tempObject = new THREE.Object3D();
	const tempColor = new THREE.Color();
	const gridPointsData: PhotonHeatmapCellData[] = [];
	let instanceIndex = 0;

	const zUp = new THREE.Vector3(0, 0, 1);
	const tangent = new THREE.Vector3();
	const bitangent = new THREE.Vector3();

	for (const [cellKey, bin] of bins.entries()) {
		const irradianceWm2 = irradianceByCellKey.get(cellKey) ?? 0;
		const illuminanceLux = luxByCellKey.get(cellKey) ?? 0;
		const clampedEnergy = Math.max(displayMinEnergy, Math.min(displayMaxEnergy, irradianceWm2));
		const normalized = (clampedEnergy - displayMinEnergy) / normalizationRange;
		tempColor.copy(getHeatmapColor(normalized));
		instancedMesh.setColorAt(instanceIndex, tempColor);

		// Build local coordinate frame for plane orientation.
		// For horizontal surfaces (floor/terrain), always use a fixed world-aligned tangent
		// so that all tiles share the same rotation, giving a uniform grid.
		const isHorizontalSurface = bin.group === 'floor' || bin.group === 'roof' || bin.group === 'terrain';
		if (isHorizontalSurface) {
			tangent.set(1, 0, 0);
			bitangent.set(0, 1, 0);
		} else {
			tangent.crossVectors(zUp, bin.normal);
			if (tangent.lengthSq() < 1e-8) {
				tangent.set(1, 0, 0);
			}
			tangent.normalize();
			bitangent.crossVectors(bin.normal, tangent).normalize();
		}

		tempObject.matrix.makeBasis(tangent, bitangent, bin.normal);
		let offsetDistance = floorSurfaceOffsetDistance;
		if (bin.group === 'facade') {
			offsetDistance = facadeSurfaceOffsetDistance;
		} else if (bin.group === 'terrain') {
			offsetDistance = terrainSurfaceOffsetDistance;
		} else if (bin.group === 'roof') {
			offsetDistance = roofSurfaceOffsetDistance;
		}
		const offsetVector =
			bin.group === 'facade'
				? bin.normal.clone().normalize().multiplyScalar(offsetDistance)
				: new THREE.Vector3(0, 0, offsetDistance);
		tempObject.matrix.setPosition(
			bin.point.clone().add(offsetVector)
		);
		tempObject.position.setFromMatrixPosition(tempObject.matrix);
		tempObject.quaternion.setFromRotationMatrix(tempObject.matrix);
		tempObject.scale.set(patchSize, patchSize, 1);
		tempObject.updateMatrix();

		instancedMesh.setMatrixAt(instanceIndex, tempObject.matrix);
		gridPointsData[instanceIndex] = {
			point: bin.point.clone(),
			normal: {
				x: bin.normal.x,
				y: bin.normal.y,
				z: bin.normal.z,
			},
			energy: irradianceWm2,
			irradianceWm2,
			illuminanceLux,
			photonCount: bin.count,
			group: bin.group,
			cellSize: photonHeatmapCellSize,
			normalizedEnergy: normalized,
			cellKey,
			photonPositions: bin.photonPositions.map((position) => position.clone()),
		};
		instanceIndex++;
	}

	instancedMesh.count = instanceIndex;
	instancedMesh.instanceMatrix.needsUpdate = true;
	if (instancedMesh.instanceColor) {
		instancedMesh.instanceColor.needsUpdate = true;
	}
	instancedMesh.userData.gridPointsData = gridPointsData;

	group.add(instancedMesh);
	scene.add(group);
	photonHeatmapGroup = group;

	photonHeatmapLegendStats = {
		enabled: true,
		cellCount: bins.size,
		minEnergy,
		maxEnergy,
		cellSize: photonHeatmapCellSize,
		normalizationMode: photonHeatmapNormalizationMode,
		displayMinEnergy,
		displayMaxEnergy,
	};
}

/**
 * Enable or disable heatmap rendering
 */
export function setPhotonEnergyHeatmapEnabled(scene: THREE.Scene, enabled: boolean): void {
	photonHeatmapEnabled = enabled;
	if (!enabled) {
		removePhotonHeatmapFromScene(scene);
		photonHeatmapLegendStats = createHeatmapLegendStats(
			photonHeatmapCellSize,
			photonHeatmapNormalizationMode,
			{ enabled: false }
		);
		return;
	}
	void renderPhotonEnergyHeatmap(scene);
}

/**
 * Change normalization mode and re-render
 */
export function setPhotonEnergyHeatmapNormalizationMode(
	scene: THREE.Scene,
	mode: PhotonHeatmapNormalizationMode
): void {
	photonHeatmapNormalizationMode = mode;
	if (photonHeatmapEnabled) {
		void renderPhotonEnergyHeatmap(scene);
	}
}

/**
 * Change grid cell size and re-render
 */
export function setPhotonEnergyHeatmapCellSize(scene: THREE.Scene, cellSize: number): void {
	photonHeatmapCellSize = Math.max(1, Math.min(50, Math.round(cellSize)));
	if (photonHeatmapEnabled) {
		void renderPhotonEnergyHeatmap(scene);
	}
}

/**
 * Force heatmap re-render (e.g. after photon map changes)
 */
export function refreshPhotonEnergyHeatmap(scene: THREE.Scene): void {
	if (!photonHeatmapEnabled) return;
	void renderPhotonEnergyHeatmap(scene);
}

/**
 * Get current legend statistics
 */
export function getPhotonEnergyHeatmapLegendStats(): PhotonHeatmapLegendStats {
	return { ...photonHeatmapLegendStats };
}
